import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  UserCheck, UserX, Download, FileText, Clock, User,
  Loader2, AlertCircle, Check, X, PenTool
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PendingUsersPage = () => {
  const { token } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Signed documents state
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [sigDialogUser, setSigDialogUser] = useState(null);
  const [userSignatures, setUserSignatures] = useState([]);
  const [loadingSigs, setLoadingSigs] = useState(false);

  const authHeaders = useCallback(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const fetchPendingUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pending-users`, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPendingUsers(data.pending_users || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending users:', err);
      toast.error('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  const handleDownloadAttachment = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pending-users/${userId}/attachment`, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (res.ok) {
        const blob = await res.blob();
        const contentDisposition = res.headers.get('content-disposition');
        const filename = contentDisposition?.split('filename=')[1] || 'attachment';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.replace(/"/g, '');
        a.click();
        URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download attachment');
      }
    } catch (err) {
      toast.error('Failed to download attachment');
    }
  };

  const openSignedDocsDialog = async (user) => {
    setSigDialogUser(user);
    setSigDialogOpen(true);
    setLoadingSigs(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/registration-signatures?email=${encodeURIComponent(user.email)}`, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (res.ok) {
        const sigs = await res.json();
        setUserSignatures(sigs);
      } else {
        setUserSignatures([]);
      }
    } catch {
      setUserSignatures([]);
    } finally {
      setLoadingSigs(false);
    }
  };

  const handleDownloadSignedDoc = async (signatureId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/registration-signatures/${signatureId}/download`, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (res.ok) {
        const blob = await res.blob();
        const contentDisposition = res.headers.get('content-disposition');
        const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || 'signed_document';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Download started');
      } else {
        toast.error('Failed to download signed document');
      }
    } catch {
      toast.error('Failed to download signed document');
    }
  };

  const openActionDialog = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setComment('');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedUser || !actionType) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pending-users/${selectedUser.user_id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          action: actionType,
          comment: comment || null
        })
      });

      if (res.ok) {
        toast.success(`User ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
        setActionDialogOpen(false);
        fetchPendingUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || `Failed to ${actionType} user`);
      }
    } catch (err) {
      toast.error(`Failed to ${actionType} user`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (isoString) => new Date(isoString).toLocaleString();
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserCheck className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pending User Approvals
            </h1>
          </div>
          <div className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-sm text-sm">
            {pendingUsers.length} pending
          </div>
        </div>

        {/* Info Banner */}
        <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium">Review Registration Requests</p>
            <p className="text-white/60 text-sm mt-1">
              Users who registered publicly are waiting for your approval. Review their attached documents and signed registration forms before approving access.
            </p>
          </div>
        </div>

        {/* Pending Users List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : pendingUsers.length === 0 ? (
          <div className="text-center py-12 bg-black/40 border border-white/10 rounded-sm">
            <UserCheck className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">No Pending Approvals</h3>
            <p className="text-white/40">All user registrations have been processed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <div
                key={user.user_id}
                className="p-6 bg-black/40 border border-white/10 rounded-sm"
                data-testid={`pending-user-${user.user_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary text-lg font-semibold">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{user.name}</h3>
                        <p className="text-white/60">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex items-center gap-6 text-white/40 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Registered: {formatDate(user.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        Requested Role: {user.role}
                      </span>
                    </div>

                    {/* Attachment Info */}
                    {user.registration_attachment && (
                      <div className="mt-4 p-3 bg-white/5 rounded-sm border border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            <div>
                              <p className="text-white text-sm font-medium">
                                {user.registration_attachment.filename}
                              </p>
                              <p className="text-white/40 text-xs">
                                {formatFileSize(user.registration_attachment.size)} 
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadAttachment(user.user_id)}
                            className="border-white/10 text-white hover:bg-white/5"
                            data-testid={`download-attachment-${user.user_id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* View Signed Documents Button */}
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openSignedDocsDialog(user)}
                        className="border-primary/30 text-primary hover:bg-primary/10"
                        data-testid={`view-signed-docs-${user.user_id}`}
                      >
                        <PenTool className="w-4 h-4 mr-2" />
                        View Signed Registration Documents
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => openActionDialog(user, 'approve')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`approve-user-${user.user_id}`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve Access
                  </Button>
                  <Button
                    onClick={() => openActionDialog(user, 'reject')}
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`reject-user-${user.user_id}`}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Confirmation Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {actionType === 'approve' ? (
                  <UserCheck className="w-5 h-5 text-green-400" />
                ) : (
                  <UserX className="w-5 h-5 text-red-400" />
                )}
                {actionType === 'approve' ? 'Approve User' : 'Reject User'}
              </DialogTitle>
              <DialogDescription className="text-white/60">
                {actionType === 'approve' 
                  ? `Grant access to ${selectedUser?.name}? They will be able to login and use the application.`
                  : `Reject registration for ${selectedUser?.name}? They will not be able to access the application.`
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-white/80">
                  {actionType === 'approve' ? 'Comment (optional)' : 'Rejection Reason (optional)'}
                </Label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={actionType === 'approve' ? 'Add any notes...' : 'Reason for rejection...'}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setActionDialogOpen(false)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAction}
                  disabled={submitting}
                  className={`flex-1 ${
                    actionType === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  } text-white`}
                  data-testid="confirm-action-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {actionType === 'approve' ? 'Approve' : 'Reject'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Signed Registration Documents Dialog */}
        <Dialog open={sigDialogOpen} onOpenChange={setSigDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <PenTool className="w-5 h-5 text-primary" />
                Signed Documents
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Registration documents signed by {sigDialogUser?.name} ({sigDialogUser?.email})
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-3 max-h-[400px] overflow-auto">
              {loadingSigs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
              ) : userSignatures.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No signed documents found for this user</p>
                </div>
              ) : (
                userSignatures.map((sig) => (
                  <div key={sig.signature_id} className="p-4 bg-white/5 border border-white/10 rounded-sm" data-testid={`signed-doc-${sig.signature_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{sig.document_title || 'Registration Document'}</p>
                        <p className="text-white/40 text-xs mt-1">
                          Signed: {new Date(sig.signed_at).toLocaleString()}
                        </p>
                        <p className="text-white/30 text-xs">{sig.document_filename || ''}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        {sig.signature_data && (
                          <img src={sig.signature_data} alt="Signature" className="h-8 object-contain bg-white rounded-sm px-2 border border-white/10" />
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadSignedDoc(sig.signature_id)}
                          className="border-white/10 text-white hover:bg-white/5 h-8"
                          data-testid={`download-signed-${sig.signature_id}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PendingUsersPage;
